import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./game.css";

type Status = "idle" | "wait" | "go" | "done";
type Tone = Status | "fast" | "slow";
type CardVars = React.CSSProperties & { ["--signal-progress"]?: string };

type LeaderboardEntry = {
  name: string;
  average: number;
  rounds?: number[];
  timestamp?: number;
};

type SignalDelayBucket = {
  weight: number;
  min: number;
  max: number;
  curve?: number;
};

const TARGET_MS = 250;
const MAX_ROUNDS = 3;
const EARLY_ABORT_LIMIT = 3;
const LEADERBOARD_REFRESH_INTERVAL_MS = 4000;
const WORSE_RECORD_MESSAGE = "더 안좋아졌어요 실시간으로 늙으셨네요";

const OVERTAKE_MESSAGES: Array<(names: string) => string> = [
  (names) => `${names}님을 죽이고 올라왔습니다!`,
  (names) => `${names}님을 모래바람 속에 묻어두고 순위를 갈아엎었습니다!`,
  (names) => `${names}님의 모자를 전리품 삼아 한 계단 위로 뛰어올랐습니다!`,
  (names) => `${names}님이 떨군 부츠 위를 밟고 순위를 빼앗았습니다!`,
  (names) => `${names}님이 지키던 자리에서 승리의 깃발을 빼앗았습니다!`,
];

const SELF_UPDATE_MESSAGES: string[] = [
  "순위는 그대로지만 사막 어딘가에서 혼자 땀은 흘렸네요.",
  "아직 순위는 그대로지만 열심히는 했네요.",
  "순위는 그대로, 자존심만 살짝 덜 깨졌습니다.",
  "자리 지키기에 성공! 그렇다고 칭찬을 기대하긴 어렵습니다.",
  "순위는 멈췄지만 연습 일지 한 줄은 채웠습니다.",
];

const SIGNAL_DELAY_BUCKETS: SignalDelayBucket[] = [
  { weight: 0.25, min: 80, max: 600, curve: 2.3 },
  { weight: 0.3, min: 600, max: 1800, curve: 1.4 },
  { weight: 0.25, min: 1800, max: 3600, curve: 1.05 },
  { weight: 0.2, min: 3600, max: 6500, curve: 0.85 },
];

const SIGNAL_EXTRA_DELAY_CHANCE = 0.32;
const SIGNAL_EXTRA_DELAY_MIN = 200;
const SIGNAL_EXTRA_DELAY_MAX = 1800;
const SIGNAL_JITTER_RANGE = 220; // +/-110ms

const SIGNAL_DELAY_MIN = SIGNAL_DELAY_BUCKETS[0].min;
const SIGNAL_DELAY_MAX =
  SIGNAL_DELAY_BUCKETS[SIGNAL_DELAY_BUCKETS.length - 1].max + SIGNAL_EXTRA_DELAY_MAX;

function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function formatNameList(names: string[]): string {
  if (names.length === 0) return "누군가";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]}와 ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} 그리고 ${names[names.length - 1]}`;
}

function getOvertakenNames(
  prevEntries: LeaderboardEntry[],
  nextEntries: LeaderboardEntry[],
  playerName: string,
): string[] {
  const previousIndex = prevEntries.findIndex((entry) => entry.name === playerName);
  const nextIndex = nextEntries.findIndex((entry) => entry.name === playerName);

  if (previousIndex === -1 || nextIndex === -1 || nextIndex >= previousIndex) {
    return [];
  }

  const defeated: string[] = [];
  for (let idx = nextIndex; idx < previousIndex; idx += 1) {
    const candidate = prevEntries[idx];
    if (candidate && candidate.name !== playerName) {
      defeated.push(candidate.name);
    }
  }
  return defeated;
}

function getOvertakeMessage(overtakenNames: string[]): string {
  const formatted = formatNameList(overtakenNames);
  return pickRandom(OVERTAKE_MESSAGES)(formatted);
}

function getSelfUpdateMessage(): string {
  return pickRandom(SELF_UPDATE_MESSAGES);
}

function normalizeLeaderboardEntries(entries: LeaderboardEntry[]): LeaderboardEntry[] {
  const bestByName = new Map<string, LeaderboardEntry>();

  entries.forEach((entry) => {
    const existing = bestByName.get(entry.name);
    if (!existing || entry.average < existing.average) {
      bestByName.set(entry.name, entry);
    }
  });

  return Array.from(bestByName.values())
    .sort((a, b) => a.average - b.average)
    .slice(0, 10);
}

function getWeightedSignalDelay(): number {
  const totalWeight = SIGNAL_DELAY_BUCKETS.reduce((sum, bucket) => sum + bucket.weight, 0);
  let target = Math.random() * totalWeight;

  for (const bucket of SIGNAL_DELAY_BUCKETS) {
    target -= bucket.weight;
    if (target <= 0) {
      const span = bucket.max - bucket.min;
      const curve = bucket.curve ?? 1;
      const random = curve === 1 ? Math.random() : Math.pow(Math.random(), curve);
      return Math.round(bucket.min + span * random);
    }
  }

  const fallback = SIGNAL_DELAY_BUCKETS[SIGNAL_DELAY_BUCKETS.length - 1];
  return Math.round(fallback.min + Math.random() * (fallback.max - fallback.min));
}

const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "development" ? "https://xn--eh3bw3h.kro.kr" : "";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? DEFAULT_API_BASE_URL).replace(
  /\/$/,
  "",
);
const LEADERBOARD_ENDPOINT = API_BASE_URL ? `${API_BASE_URL}/api/leaderboard` : "/api/leaderboard";
const LEADERBOARD_TOKEN_ENDPOINT = API_BASE_URL
  ? `${API_BASE_URL}/api/leaderboard-token`
  : "/api/leaderboard-token";

const STATUS_LABEL: Record<Status, string> = {
  idle: "결투 대기",
  wait: "신호 대기",
  go: "사격 준비",
  done: "결과",
};

const STATUS_HELPER: Record<Status, string> = {
  idle: "숨을 고르고 결투를 시작해 보세요.",
  wait: "초록 신호가 켜질 때까지 손을 떼고 기다리세요.",
  go: "지금 눌러야 승부가 갈립니다!",
  done: "결과를 확인하고 다음 판을 준비하세요.",
};

const BUTTON_TEXT: Record<Status, string> = {
  idle: "결투 시작",
  wait: "아직 기다리는 중",
  go: "지금 눌러!",
  done: "다음 판",
};

const SUCCESS_MESSAGES: string[] = [
  "상대 모자가 날아가고 사막은 당신을 위해 박수를 쳤습니다!",
  "방아쇠 소리에 선술집 피아노가 멈추고 모두가 당신 이름을 외쳤습니다!",
  "카드 한 장이 총탄에 찢어지며 딜러가 항복 깃발을 들었습니다!",
  "모래먼지 속에서도 당신의 총알은 상대 부츠에 구멍을 냈습니다!",
  "황금빛 석양 아래에서 보안관이 당신에게 별을 하나 더 달아줍니다!",
  "불량배의 시가가 반으로 잘리며 연기가 패배를 인정했습니다!",
  "사막에 울린 한 발에 선인장도 박수를 쳤습니다!",
  "상대 총알보다 빠르게 그림자만 남기고 승리했습니다!",
  "황소가 당신의 속도에 감탄해 마차를 끌어다 줬습니다!",
  "동전이 땅에 닿기도 전에 승부가 끝났습니다!",
  "검붉은 하늘이 당신을 주인공으로 기억합니다!",
  "도박장 딜러가 오늘은 쉬라며 술 한 잔을 건넵니다!",
  "기차가 멈춰서 당신의 승리를 확인했습니다!",
  "사막의 바람까지 당신 편으로 불어줍니다!",
  "관객이 모래 위에 당신 이름을 새기고 있습니다!",
  "상대 총알은 아직도 굴비처럼 줄지어 오고 있네요!",
  "불량배가 떨어뜨린 모자 속에서 사과 편지가 나왔습니다!",
  "빛보다 빠른 손놀림에 시계가 멈춰 섰습니다!",
  "결투장 고양이도 당신 편에 줄 섰습니다!",
  "당신의 총성이 별빛을 깨워 마을을 밝힙니다!",
  "상대는 총 대신 흰 깃발을 꺼냈습니다!",
  "선술집 종업원이 오늘 술값 면제라고 합니다!",
  "모래먼지에 숨겨진 총알이 정확히 목표를 맞혔습니다!",
  "카우보이 모닥불이 당신을 위해 더 크게 타오릅니다!",
  "사막 너머까지 소문이 퍼져 도전자들이 줄을 섭니다!",
  "검에 먼지가 쌓이기 전에 승부가 끝났습니다!",
  "당신의 총알은 상대 귀를 스치며 귀걸이를 만들어 줬습니다!",
  "한 번의 방아쇠로 포커판까지 쓸어 버렸습니다!",
  "마을 방송국이 승전보를 송출하고 있습니다!",
  "사막 거북이도 당신보다 느리다며 좌절했습니다!",
];

const FAIL_MESSAGES: string[] = [
  "당신의 총은 세상에서 가장 느린 발사체였고, 그 사이에 상대는 이미 승리를 통과한 뒤였습니다.",
  "방아쇠를 당기기 전에, 이미 상대는 '이제 그만하자'고 고백할 준비를 마쳤죠. 승리는 당신에게 한 번도 다가가지 않았습니다.",
  "당신의 총이 발사되기도 전에, 상대는 벌써 승리를 흡수하고 있었습니다. 당신은 그저 먼지 속에서 패배를 바라봤죠.",
  "상대의 겨드랑이에 총알이 스쳤지만 겁을 먹고 도망갔습니다!",
  "당신이 쓰러졌고 가족들은 당신을 쏜 사람과 재혼했습니다. 어쩌면 필요 없는 존재였을지도?",
  "방아쇠가 낯선 듯 손가락이 헤매네요.",
  "혹시 조정간이 아직 안전 모드인가요?",
  "이런! 총을 뽑기도 전에 모자가 먼저 날아갔어요.",
  "총알보다 빠른 건 당신 표정이었습니다.",
  "사막 바람이 당신을 놀리며 모래를 뿌렸습니다.",
  "방청객이 하품하다 하품에 총알이 맞았습니다.",
  "상대가 웃다가 이겼습니다. 당신 덕분이에요.",
  "혹시 물총을 챙겨온 건 아닐까요?",
  "도박장에서도 이런 배수진은 본 적이 없다고 하네요.",
  "스테이크보다 느리게 익는 속도였습니다.",
  "모래시계가 네 번이나 뒤집혔습니다.",
  "상대가 총 대신 손수건을 꺼냈는데도 졌어요.",
  "광산마저 당신 손속에 실망했습니다.",
  "사막 소나기보다 느린 반응을 보여줬네요.",
  "오늘따라 방아쇠가 양보를 좋아하나 봅니다.",
  "관객들이 당신 대신 모금을 시작했습니다.",
  "선인장이 당신보다 먼저 총을 뽑았습니다.",
  "상대의 재채기에 놀라 패했습니다.",
  "바람이 '다음엔 연습 좀 하라'고 속삭였습니다.",
  "마을 신문이 '세상에서 가장 느린 총잡이'라고 보도했어요.",
  "모닥불이 꺼지기도 전에 이미 져버렸네요.",
  "장전보다 한숨이 빨랐습니다.",
  "상대가 하품하며 삼연발을 날렸습니다.",
  "마차 바퀴도 당신보다 빠르게 돌아요.",
  "사막 거북이가 당신 대신 미안하다고 전했습니다.",
  "손목 보호대가 울면서 도망쳤습니다.",
  "총잡이들 사이에서 '사막의 느림보'로 불리고 있습니다.",
  "상대가 총을 넣을 때 즈음 당신은 장전을 시작하더군요.",
  "오늘 기록은 기념비적입니다. '전광석화의 반대말'로요.",
  "모래시계 속 모래가 당신을 비웃고 있어요.",
  "총을 당기기 전에, 당신은 부모님의 마음을 다시 돌리려 했습니까? 아니면 그저 무기력하게 시간을 낭비한 건 아닌가요?",
  "연습을 게을리 했던 지난날들이 지금의 당신을 만들었습니다. 총을 뽑기 전에, 이미 그 기회는 사라졌습니다.",
  "방아쇠를 당기기 전, 당신의 손끝은 오랜 나태함과 방황에 묶여 있었고, 그제야 총알이 흘러나갔습니다.",
  "연습을 멈췄던 그 순간, 이제 총을 뽑을 때마다 그 나태함이 총알보다 더 빨리 당신을 찾아옵니다.",
  "어릴 때 부모님의 기대를 무시했을 때처럼, 당신은 오늘도 중요한 순간을 놓쳤고, 그 손끝은 아무것도 할 수 없었습니다.",
  "방아쇠를 당기기 전에, 이미 당신은 스스로에게 너무 많은 기회를 주지 않았죠. 그만큼 나태한 시간을 보낸 대가가 돌아온 거예요.",
  "총을 쥐었지만, 그 손에 들려 있는 건 과거의 나태함과 무관심뿐이었습니다. 총알은 이미 사라졌고, 당신은 여전히 멍하니 서 있었죠.",
  "총을 겨누기 전에, 당신은 이미 자신에게 너무 많은 변명을 했고, 결국 그 변명 속에서 패배가 자리를 잡았죠.",
  "당신이 방아쇠를 당기고 나서야, '왜 이렇게 느렸을까?' 하는 후회가 밀려왔습니다. 그 후회는 이미 연습하지 않았던 시간이 만들어낸 결과였죠.",
  "오늘의 패배는 단순히 총을 놓쳤기 때문이 아닙니다. 당신은 이미 삶에서 중요한 기회를 놓쳤고, 그게 지금 총알보다 빠르게 당신을 비웃고 있습니다.",
  "당신이 총을 겨누기 전에, 얼마나 많은 날들을 낭비했나요? 그 날들의 결과가 바로 지금의 패배로 돌아왔습니다.",
  "연습 없이 시간을 보낸 자리에, 이제 총알은 아무것도 하지 않고 사라지기만 합니다. 그 자리를 비운 건 바로 나태했던 지난날들입니다.",
  "당신이 방아쇠를 당기지 않던 그 순간, 부모님은 이미 '자식이 무슨 일이라도 할까?'라며 한숨을 쉬었을 겁니다.",
  "그저 어리석게 시간을 보냈고, 그 시간은 이제 당신을 놓치고 총을 겨누는 대신 패배를 그대로 쥐어줬습니다.",
  "방아쇠를 당기고 나서야 깨달았을 겁니다. '내가 연습할 때였는데.' 그 깨달음은 이미 너무 늦었고, 총알은 상대를 놓쳤죠.",
  "가족들의 기대를 저버린 것처럼, 당신은 오늘도 중요한 순간을 놓쳤고, 그 총알은 어쩌면 당신의 마지막 기회였을지도 모릅니다.",
  "총을 겨누기 전에, '오늘도 적당히'라는 말이 당신을 지배했고, 결국 그 말은 총알보다 먼저 당신을 떠났습니다."

];

export default function GamePage() {
  const [status, setStatus] = useState<Status>("idle");
  const [results, setResults] = useState<number[]>([]);
  const [playerName, setPlayerName] = useState("");
  const [savedName, setSavedName] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [personalBest, setPersonalBest] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);
  const [wasAborted, setWasAborted] = useState(false);
  const [rankingMessage, setRankingMessage] = useState<string | null>(null);
  const [earlyAbortCount, setEarlyAbortCount] = useState(0);

  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastSignalDelayRef = useRef<number>(0);

  const nextSignalDelay = useCallback(() => {
    let delay = getWeightedSignalDelay();

    if (Math.random() < SIGNAL_EXTRA_DELAY_CHANCE) {
      const extraSpan = SIGNAL_EXTRA_DELAY_MAX - SIGNAL_EXTRA_DELAY_MIN;
      delay += SIGNAL_EXTRA_DELAY_MIN + Math.random() * extraSpan;
    }

    const jitter = (Math.random() - 0.5) * SIGNAL_JITTER_RANGE;
    delay = Math.round(delay + jitter);

    delay = Math.min(SIGNAL_DELAY_MAX, Math.max(SIGNAL_DELAY_MIN, delay));

    lastSignalDelayRef.current = delay;
    return delay;
  }, []);

  const requestSubmissionToken = useCallback(async (roundValues: number[]) => {
    try {
      const response = await fetch(LEADERBOARD_TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rounds: roundValues }),
      });
      let payload: any = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }
      if (!response.ok) {
        const errorMessage =
          (typeof payload?.error === "string" && payload.error.trim()) ||
          "리더보드 토큰을 발급받지 못했습니다.";
        throw new Error(errorMessage);
      }
      if (typeof payload?.token !== "string" || !payload.token) {
        throw new Error("리더보드 토큰을 발급받지 못했습니다.");
      }
      return payload.token as string;
    } catch (error: any) {
      throw new Error(error?.message ?? "리더보드 토큰 발급에 실패했습니다.");
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(LEADERBOARD_ENDPOINT);
      if (!response.ok) throw new Error("리더보드를 불러오지 못했어요.");
      const data = await response.json();
      const entries: LeaderboardEntry[] = data.entries ?? [];
      const normalized = normalizeLeaderboardEntries(entries);
      setLeaderboard(normalized);
      return normalized;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
    const intervalId = window.setInterval(() => {
      if (!document.hidden) {
        fetchLeaderboard();
      }
    }, LEADERBOARD_REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchLeaderboard]);

  useEffect(() => {
    if (!savedName) return;
    const matchedEntry = leaderboard.find((entry) => entry.name === savedName);
    if (matchedEntry) {
      setPersonalBest((prev) => (prev === null ? matchedEntry.average : Math.min(prev, matchedEntry.average)));
    }
  }, [leaderboard, savedName]);

  const resetTimer = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const resetMatch = () => {
    resetTimer();
    setStatus("idle");
    setResults([]);
    setPlayerName(savedName ?? "");
    setWasAborted(false);
    setEarlyAbortCount(0);
  };

  const startRound = () => {
    resetTimer();
    setStatus("wait");
    setWasAborted(false);
    const delay = nextSignalDelay();
    timerRef.current = window.setTimeout(() => {
      setStatus("go");
      startTimeRef.current = performance.now();
    }, delay);
  };

  const average = useMemo(() => {
    if (results.length !== MAX_ROUNDS) return null;
    return Math.round(results.reduce((sum, value) => sum + value, 0) / MAX_ROUNDS);
  }, [results]);

  const hasSavedRecord = savedName !== null;
  const hasAverage = average !== null;
  const canShowUpdateButton =
    hasSavedRecord && hasAverage && personalBest !== null && average < personalBest;
  const shouldShowSubmitButton = hasAverage && (!hasSavedRecord || canShowUpdateButton);
  const needsBetterScoreHint =
    hasSavedRecord && hasAverage && personalBest !== null && average >= personalBest;

  const lastResult = results.length ? results[results.length - 1] : null;

  const tone: Tone =
    status === "done"
      ? average !== null
        ? average <= TARGET_MS
          ? "fast"
          : "slow"
        : lastResult !== null && lastResult <= TARGET_MS
          ? "fast"
          : "slow"
      : status;

  const signalProgress = status === "go" ? 1 : 0;

  const cardStyle: CardVars = useMemo(
    () => ({
      "--signal-progress": signalProgress.toFixed(2),
    }),
    [signalProgress],
  );

  const recordResult = (elapsed: number) => {
    setResults((prev) => {
      const next = [...prev, elapsed];
      return next.slice(0, MAX_ROUNDS);
    });
  };

  const reactNow = () => {
    if (status !== "go") {
      return;
    }
    const elapsed = Math.round(performance.now() - startTimeRef.current);
    resetTimer();
    recordResult(elapsed);
    setStatus("done");
    setEarlyAbortCount(0);
  };

  const abortRound = () => {
    resetTimer();
    setStatus("idle");
    setWasAborted(true);
  };

  const handleTrigger = () => {
    if (status === "go") {
      reactNow();
      return;
    }
    if (status === "wait") {
      window.alert("아직 신호가 오지 않았어요! 초록 불이 켜지면 눌러주세요.");
      abortRound();
      const nextAbortCount = earlyAbortCount + 1;
      if (nextAbortCount >= EARLY_ABORT_LIMIT) {
        setEarlyAbortCount(0);
        window.alert("3회 연속으로 신호 전에 눌렀습니다. 판이 초기화됩니다.");
        resetMatch();
      } else {
        setEarlyAbortCount(nextAbortCount);
      }
      return;
    }
    if (status === "idle") {
      startRound();
      return;
    }
    if (status === "done") {
      if (results.length >= MAX_ROUNDS) {
        resetMatch();
      } else {
        startRound();
      }
    }
  };

  const helperText = STATUS_HELPER[status];
  const statusLabel = STATUS_LABEL[status];

  const buttonLabel = (() => {
    if (status === "idle") return wasAborted ? "다시 시작" : BUTTON_TEXT.idle;
    if (status === "wait") return BUTTON_TEXT.wait;
    if (status === "go") return BUTTON_TEXT.go;
    if (status === "done") {
      return results.length >= MAX_ROUNDS ? "새 결투" : "다음 판";
    }
    return "결투 시작";
  })();

  const customResultMessage = useMemo(() => {
    if (status !== "done" || results.length === 0) return null;
    const lastValue = results[results.length - 1];
    const pool = lastValue <= TARGET_MS ? SUCCESS_MESSAGES : FAIL_MESSAGES;
    return pool[Math.floor(Math.random() * pool.length)];
  }, [status, results]);

  const roundStates = useMemo(() => {
    return Array.from({ length: MAX_ROUNDS }, (_, index) => {
      if (index < results.length) {
        return results[index] <= TARGET_MS ? "win" : "lose";
      }
      if (index === results.length) {
        if (status === "wait") return "wait";
        if (status === "go") return "active";
        return "pending";
      }
      return "pending";
    });
  }, [results, status]);

  const submitScore = async () => {
    if (average === null) {
      setSubmissionMessage("아직 평균 기록이 없어요.");
      return;
    }

    const effectiveName = (savedName ?? playerName).trim();
    if (!effectiveName) {
      setSubmissionMessage("이름을 입력해 주세요.");
      return;
    }

    const previousEntries = leaderboard;
    const wasUpdate = savedName !== null;
    const knownBest =
      personalBest ??
      previousEntries.find((entry) => entry.name === effectiveName)?.average ??
      null;

    if (wasUpdate && knownBest !== null && !(average < knownBest)) {
      setSubmissionMessage(WORSE_RECORD_MESSAGE);
      setRankingMessage(null);
      return;
    }

    setIsSubmitting(true);
    setSubmissionMessage(null);
    setRankingMessage(null);

    try {
      const submissionToken = await requestSubmissionToken(results);
      const response = await fetch(LEADERBOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: effectiveName, average, rounds: results, token: submissionToken }),
      });

      if (!response.ok) {
        let errorMessage = "리더보드 등록에 실패했습니다.";
        try {
          const payload = await response.json();
          if (typeof payload?.error === "string" && payload.error.trim()) {
            errorMessage = payload.error;
          }
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      setSavedName(effectiveName);
      setPlayerName(effectiveName);
      setPersonalBest((prev) => (prev === null || average < prev ? average : prev));

      const updatedEntries = (await fetchLeaderboard()) ?? previousEntries;

      if (wasUpdate) {
        const previousRank = previousEntries.findIndex((entry) => entry.name === effectiveName);
        const nextRank = updatedEntries.findIndex((entry) => entry.name === effectiveName);
        const overtakenNames = getOvertakenNames(previousEntries, updatedEntries, effectiveName);

        if (nextRank !== -1 && previousRank !== -1 && nextRank < previousRank) {
          setRankingMessage(getOvertakeMessage(overtakenNames));
        } else {
          setRankingMessage(getSelfUpdateMessage());
        }

        setSubmissionMessage("최종평균에 순위 갱신 !");
      } else {
        setRankingMessage(null);
        setSubmissionMessage("리더보드에 등록되었습니다!");
      }
    } catch (error: any) {
      setRankingMessage(null);
      setSubmissionMessage(error?.message ?? "등록에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="game-root" data-tone={tone}>
      <div className="game-stage">
        <div className="game-card" style={cardStyle}>
          <div className="signal-block">
            <div className="chip-row">
              <p className="status-chip">{statusLabel}</p>
              <div className="beacon-row" aria-hidden="true">
                {roundStates.map((state, idx) => (
                  <span key={`round-dot-${idx}`} className={`round-dot ${state}`} />
                ))}
              </div>
            </div>
            <p className="signal-copy">
              초록 신호가 켜지는 즉시 클릭하고 <strong>{TARGET_MS}ms</strong> 안에 고르면 명사수로 인정받습니다.
            </p>
          </div>

          <div className={`result-panel ${status === "done" && lastResult !== null && lastResult <= TARGET_MS ? "good" : status === "done" ? "slow" : "standby"}`}>
            <p className="result-heading">{status === "done" ? "방금 기록" : "다음 기록"}</p>
            <p className="result-value">
              <span className="result-digits">{status === "done" && lastResult !== null ? lastResult : "--"}</span>
              <span className="result-unit">ms</span>
            </p>
            <p className="result-message">
              {status === "done" ? (
                <span className="result-flavor">
                  {customResultMessage ??
                    (lastResult !== null && lastResult <= TARGET_MS
                      ? "승리! 오늘의 석양은 당신 것입니다."
                      : "아쉽네요... 손목을 한 번 더 풀어 볼까요?")}
                </span>
              ) : (
                helperText
              )}
            </p>
          </div>

          {average !== null && (
            <div className="series-summary">
              <h2>최종 평균</h2>
              <p className="series-average">{average}ms</p>
              <div className={`series-controls${savedName ? " saved" : ""}`}>
                {savedName ? (
                  <p className="saved-name">
                    닉네임 : <strong>{savedName}</strong>
                  </p>
                ) : (
                  <input
                    type="text"
                    value={playerName}
                    placeholder="이름을 입력해 주세요"
                    onChange={(event) => setPlayerName(event.target.value)}
                  />
                )}
                <div className="series-actions">
                  {shouldShowSubmitButton && (
                    <button type="button" disabled={isSubmitting} onClick={submitScore}>
                      {isSubmitting
                        ? savedName
                          ? "갱신 중..."
                          : "등록 중..."
                        : savedName
                          ? "리더보드 갱신"
                          : "리더보드 등록"}
                    </button>
                  )}
                </div>
              </div>
              {submissionMessage && <p className="submit-message">{submissionMessage}</p>}
              {submissionMessage === "최종평균에 순위 갱신 !" && rankingMessage && (
                <p className="submit-message rank-update">{rankingMessage}</p>
              )}
              {needsBetterScoreHint && !submissionMessage && (
                <p className="submit-message">
                  이전 최고 기록인 {personalBest}ms보다 빠를 때만 리더보드 갱신 버튼이 나타납니다.
                </p>
              )}
            </div>
          )}

          <div className="card-body">
            <button type="button" className={`trigger-button status-${status}`} onMouseDown={handleTrigger}>
              <span className="trigger-fill" aria-hidden="true" />
              <span className="trigger-label">{buttonLabel}</span>
            </button>
          </div>
        </div>

        <section className="leaderboard">
          <header>
            <h3>리더보드</h3>
            <small>상위 10명의 평균 기록</small>
          </header>
          <ol>
            {leaderboard.length === 0 && <li>아무도 없습니다.</li>}
            {leaderboard.map((entry, index) => {
              const rankClass = index < 3 ? `rank-${index + 1}` : undefined;
              return (
                <li key={`${entry.name}-${entry.timestamp ?? index}`} className={rankClass}>
                  <span className="rank">{index + 1}</span>
                  <span className="name">{entry.name}</span>
                  <span className="score">{entry.average}ms</span>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </div>
  );
}
